import { Link } from '@tanstack/react-router';
import { Pencil, Search, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationList, ConversationSummary } from '@up2d8/contracts';
import { Avatar, cn, relativeTime } from '@up2d8/ui';
import { uiLocale } from '../../../shared/format';

/**
 * Paneel 1 van het berichtenscherm: de gesprekkenlijst (replica-spec §3).
 *
 * Alles wat hier staat komt uit gegevens. De mockup toont acht rijen met namen als "Sarah De
 * Backer" en "IT-helpdesk"; die staan nergens in deze code. Een scherm dat er met voorbeelddata
 * goed uitziet en met echte data niet, is geen scherm.
 *
 * **Geen online-stip**, en ook niet de ruimte ervoor (besluit van 6 oktober, zie
 * `docs/design/deviations.md`). Een lege plek waar iets hoorde te staan, ziet er kapotter uit dan
 * een avatar zonder stip.
 */

export type ConversationFilter = 'all' | 'unread' | 'groups';

export interface DeskConversationListProps {
  data: ConversationList | undefined;
  loading: boolean;
  /** Het gesprek dat in paneel 2 openstaat; bepaalt welke rij actief is. */
  activeId: string | null;
  onNew: () => void;
}

/**
 * De tijd op een rij: vandaag het uur, gisteren "Gisteren", deze week de weekdag, ouder de datum.
 *
 * Dezelfde functie als op de berichtkaart, met `clock` voor vandaag (§3.4). Tien rijen met "2 uur
 * geleden" onder elkaar lezen als tien sommen; op een kaart is diezelfde zin juist het antwoord.
 */
function rowTime(iso: string | null, now: Date, yesterday: string): string {
  if (!iso) return '';
  return relativeTime(
    iso,
    now,
    { yesterday, justNow: '', minutesAgo: '', hoursAgo: '' },
    uiLocale(),
    'clock',
  );
}

export function DeskConversationList({
  data,
  loading,
  activeId,
  onNew,
}: DeskConversationListProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ConversationFilter>('all');

  const items = useMemo(() => data?.items ?? [], [data]);

  /*
    Filteren en zoeken gebeuren in de browser, op de lijst die er al is (BER-4).

    `GET /conversations` levert hoogstens honderd gesprekken en pagineert niet, dus de tellers in
    de filters komen uit dezelfde array als de lijst eronder en kunnen er niet van afwijken. Zodra
    die route gaat pagineren, moet dit naar de server: een filter dat alleen de geladen pagina
    doorzoekt, zegt "geen resultaten" terwijl het gesprek drie pagina's verderop staat.
  */
  const unreadCount = items.filter((item) => item.unreadCount > 0).length;

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter((item) => {
      if (filter === 'unread' && item.unreadCount === 0) return false;
      if (filter === 'groups' && item.kind !== 'group') return false;
      if (!needle) return true;
      return (
        item.title.toLowerCase().includes(needle) ||
        (item.lastMessagePreview ?? '').toLowerCase().includes(needle)
      );
    });
  }, [items, filter, query]);

  const pinned = shown.filter((item) => item.pinned);
  const recent = shown.filter((item) => !item.pinned);
  const now = new Date();

  const filters: { key: ConversationFilter; label: string; count: number | null }[] = [
    { key: 'all', label: t('messaging.filterAll'), count: null },
    { key: 'unread', label: t('messaging.filterUnread'), count: unreadCount || null },
    { key: 'groups', label: t('messaging.filterGroups'), count: null },
  ];

  return (
    <div
      data-testid="conversation-panel"
      className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-card border border-border-light bg-surface shadow-sm"
    >
      {/* §3.1 — kop met de teller en de knop "nieuw bericht" */}
      <div className="flex items-center justify-between px-[18px] pb-3 pt-[18px]">
        <h2 className="flex items-center gap-2 font-heading text-[15px] font-semibold text-text-primary">
          {t('messaging.panelTitle')}
          {data && data.totalUnread > 0 && (
            <span className="rounded-pill bg-brand-orange px-2 py-0.5 font-heading text-[10px] font-bold text-text-on-dark">
              {t('messaging.newCount', { count: data.totalUnread })}
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={onNew}
          data-testid="new-conversation"
          aria-label={t('messaging.newConversation')}
          className="flex h-[34px] w-[34px] items-center justify-center rounded-pill bg-brand-primary text-text-on-dark shadow-[0_2px_8px_rgba(0,178,221,0.3)] transition-colors hover:bg-brand-primary-dark"
        >
          <Pencil aria-hidden className="h-4 w-4" />
        </button>
      </div>

      {/* §3.2 — zoekveld */}
      <div className="relative mx-[18px] mb-3">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-[13px] top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-text-muted"
        />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          data-testid="conversation-search"
          aria-label={t('messaging.searchLabel')}
          placeholder={t('messaging.searchPlaceholder')}
          className="h-[38px] w-full rounded-pill border border-border bg-background pl-[38px] pr-[14px] text-small text-text-primary outline-none transition-all placeholder:text-text-muted focus:border-brand-primary focus:bg-surface focus:shadow-[0_0_0_4px_rgba(0,178,221,0.1)]"
        />
      </div>

      {/* §3.3 — filters met hun tellers */}
      <div className="flex gap-1.5 border-b border-border-light px-[18px] pb-3">
        {filters.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setFilter(item.key)}
            aria-pressed={filter === item.key}
            data-testid={`conversation-filter-${item.key}`}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-pill px-3 py-1.5 font-heading text-caption font-medium transition-colors',
              filter === item.key
                ? 'bg-brand-primary-tint font-semibold text-brand-primary-dark'
                : 'text-text-secondary hover:bg-background hover:text-brand-primary',
            )}
          >
            {item.label}
            {item.count !== null && (
              <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-pill bg-brand-orange px-1 text-[9px] font-bold text-text-on-dark">
                {item.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* §3.4 — de lijst; dit is het enige dat scrolt */}
      <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2 pb-2.5 pt-1.5">
        {loading ? (
          <p className="px-2.5 py-4 text-small text-text-muted">{t('common.loading')}</p>
        ) : shown.length === 0 ? (
          <p className="px-2.5 py-4 text-small text-text-muted">
            {items.length === 0 ? t('messaging.empty') : t('messaging.noMatches')}
          </p>
        ) : (
          <>
            {pinned.length > 0 && (
              <>
                <GroupLabel>{t('messaging.pinnedGroup')}</GroupLabel>
                {pinned.map((item) => (
                  <Row key={item.id} item={item} active={item.id === activeId} now={now} />
                ))}
              </>
            )}
            {recent.length > 0 && (
              <>
                {/* De kop "Recent" verschijnt alleen als er iets vastgezet is; anders is het de
                    enige groep en zegt het woord niets. */}
                {pinned.length > 0 && <GroupLabel>{t('messaging.recentGroup')}</GroupLabel>}
                {recent.map((item) => (
                  <Row key={item.id} item={item} active={item.id === activeId} now={now} />
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function GroupLabel({ children }: { children: string }) {
  return (
    <p className="px-2.5 pb-1 pt-2.5 font-heading text-[10px] font-medium uppercase tracking-[0.1em] text-text-muted">
      {children}
    </p>
  );
}

function Row({ item, active, now }: { item: ConversationSummary; active: boolean; now: Date }) {
  const { t } = useTranslation();
  const unread = item.unreadCount > 0;

  /*
    Het voorvoegsel voor de voorbeeldregel (§3.4).

    "Jij:" bij een eigen bericht, de voornaam van de afzender in een groep, en niets in een
    één-op-één-gesprek van de ander — daar staat zijn naam al bovenaan de rij.

    De server levert de feiten (`lastMessageMine`, `lastMessageSenderFirstName`) en niet de zin:
    "Jij" is interfacetekst en hoort vertaald te worden waar de rest van de interface vertaald
    wordt.
  */
  const prefix = item.lastMessageMine
    ? t('messaging.youPrefix')
    : item.lastMessageSenderFirstName
      ? `${item.lastMessageSenderFirstName}:`
      : null;

  return (
    <Link
      to="/app/gesprekken/$conversationId"
      params={{ conversationId: item.id }}
      data-testid="chat-row"
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center gap-3 rounded-md p-2.5 transition-colors',
        active ? 'bg-brand-primary-tint' : 'hover:bg-background',
      )}
    >
      {/* Een persoon krijgt een ronde avatar, een groep een afgeronde tegel met icoon (§1). */}
      {item.kind === 'group' ? (
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-brand-primary-tint text-brand-primary-dark">
          <Users aria-hidden className="h-5 w-5" />
          <span className="sr-only">{t('messaging.groupLabel')}</span>
        </span>
      ) : (
        <Avatar name={item.title} size="lg" />
      )}

      <span className="min-w-0 flex-1 leading-[1.3]">
        <span className="mb-0.5 flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate font-heading text-small font-semibold text-text-primary">
            {item.title}
            {item.tenantName && (
              <span className="ml-1.5 font-body text-micro font-normal text-text-muted">
                {t('messaging.viaTenant', { tenant: item.tenantName })}
              </span>
            )}
          </span>
          <span
            className={cn(
              'shrink-0 text-micro',
              unread ? 'font-semibold text-brand-orange-deep' : 'text-text-muted',
            )}
          >
            {rowTime(item.lastMessageAt, now, t('feed.yesterday'))}
          </span>
        </span>
        <span className="flex items-center gap-2">
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-caption',
              unread ? 'font-medium text-text-primary' : 'text-text-secondary',
            )}
          >
            {prefix && <span className="text-text-muted">{prefix} </span>}
            {item.lastMessagePreview ?? t('messaging.noMessages')}
          </span>
          {unread && (
            <span
              data-testid="chat-row-badge"
              className="flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-pill bg-brand-orange px-[5px] font-heading text-[10px] font-semibold text-text-on-dark"
            >
              {item.unreadCount}
            </span>
          )}
        </span>
      </span>
    </Link>
  );
}
